import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpResourceService from "@plannel/services/fp/fp-resource-service";
import StagePopup from "@plannel/pages/factory-plan/common/popup/StagePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import ColorPickerRenderer from "@plannel/pages/factory-plan/common/component/ColorPickerRenderer";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

// 이 화면의 고정 조회조건 2개(§10 — DB 동적 표시 제어 제거, 항상 이 순서로 전부 노출).
const SEARCH_FIELD_LABELS = { resourceCdNm: "FP_RESOURCE_CD", stageCdNm: "FP_STAGE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const { snackBarMessageColor } = ColorUtil;

// old validRules required 2개(resourceCd/stageCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "resourceCd", label: "FP_RESOURCE_CD" },
  { field: "stageCd", label: "FP_STAGE_CD" }
];

const Resource = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    resourceCdNm: reduxViewState?.resourceCdNm ?? "",
    stageCdNm: reduxViewState?.stageCdNm ?? "",
  });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [stagePopup, setStagePopup] = useState({ open: false, node: null });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [resource, setResource] = useState([]);

  // §8 공통코드 콤보 — old getCodeEditor('FP_FIFO_TP_CD') / setGridComboList(..., 'FP_PLAN_LEVEL_TP_CD')
  // 둘 다 공통코드 그룹 기반 드롭다운이라 §8 공통 헬퍼(common/grid.js)로 동일하게 처리.
  const fifoTpValuesRef = useCommonCodeValues("FP_FIFO_TP_CD");
  const planLvlValuesRef = useCommonCodeValues("FP_PLAN_LEVEL_TP_CD");

  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => FpGridSetting({ title, viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // planScope=서버, 나머지 2개(자원/스테이지 코드·명칭)는 AG Grid 외부 필터로 즉시 필터(§6-1 케이스3).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { resourceCdNm, stageCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    if (stageCdNm && !(hit(r.stageCd, stageCdNm) || hit(r.stageNm, stageCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  // 딥링크 수신(fp-master-screen-guide.md §13-2) — FLO 화면에서 자원/공구 노드를 더블클릭하면
  // 이 화면으로 이동하면서 자원 코드를 채워준다. 케이스3(클라 외부 필터)이라 setState만으로
  // 재조회/재필터가 자동으로 걸린다(§13-3).
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    setSearchFields((prev) => ({
      resourceCdNm: freshState.resourceCdNm ?? prev.resourceCdNm,
      stageCdNm: freshState.stageCdNm ?? prev.stageCdNm,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const columnDefs = [
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론이
    // number/string/boolean/date 어디에도 안 걸려 "object"로 폴백해 경고를 내므로 추론을 끈다.
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", cellClass: "stringType", width: 240 },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110 },
    { headerName: "FP_DISPLAY_SEQ", field: "displaySeq", colId: "displaySeq", width: 110, type: ["numberColumn", "rightAligned"] },
    // old setColorPickerRenderer(gridView, 'displayColor')(jquery-minicolors) 대응 —
    // 네이티브 input[type=color]로 이관(ColorPickerRenderer). hue 슬라이더/swatch 프리셋
    // 같은 장식적 UI는 유보, 색상 선택·값 반영 기능은 이관 완료.
    { headerName: "FP_DISPLAY_COLOR", field: "displayColor", colId: "displayColor", cellClass: "stringType", width: 120,
      editable: false, cellRenderer: ColorPickerRenderer, cellStyle: { padding: 0 } },
    // 스테이지 그룹(2줄 헤더, 접기/펼치기) — Route.js와 동일 FP_STAGE 그룹 패턴 재사용.
    {
      headerName: "FP_STAGE", groupId: "stage", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_STAGE_CD", field: "stageCd", colId: "stageCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setStagePopup({ open: true, node }) } },
        { headerName: "FP_STAGE_NM", field: "stageNm", colId: "stageNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" },
        { headerName: "FP_PLANT_CD", field: "plantCd", colId: "plantCd", type: ["nonEditableColumn"], width: 180, columnGroupShow: "open" },
        { headerName: "FP_PLANT_NM", field: "plantNm", colId: "plantNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" }
      ]
    },
    // 툴 자원 설정 그룹 — toolCnt는 toolResourceYn이 켜진 행에서만 편집 가능(old styleCallback 대응).
    {
      headerName: "FP_TOOL_SETTING", groupId: "toolSetting", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_TOOL_RESOURCE_YN", field: "toolResourceYn", colId: "toolResourceYn", type: ["booleanColumn", "centerAligned"], width: 120 },
        { headerName: "FP_TOOL_CNT", field: "toolCnt", colId: "toolCnt", width: 120, columnGroupShow: "open",
          type: ["numberColumn", "rightAligned"], editable: params => params.data?.toolResourceYn === true }
      ]
    },
    // 부가 정보 그룹 — fifoTpCd/planLvlTpCd는 §8 공통코드 콤보, loadYn은 old의 반전 체크박스
    // 의미를 그대로 재현(값 loadYn=false일 때 체크 표시 — old getCheckedCallback: !value).
    {
      headerName: "FP_EXTRA_INFO", groupId: "extraInfo", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_FIFO", field: "fifoTpCd", colId: "fifoTpCd", cellClass: "stringType", width: 160,
          type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
          cellEditorParams: () => codeComboCellEditorParams("FP_FIFO_TP_CD", fifoTpValuesRef, t) },
        { headerName: "FP_LOAD_YN", field: "loadYn", colId: "loadYn", type: ["booleanColumn", "centerAligned"], width: 110, columnGroupShow: "open",
          valueGetter: p => !p.data.loadYn,
          valueSetter: p => { p.data.loadYn = !p.newValue; return true; } },
        { headerName: "FP_PLAN_LVL", field: "planLvlTpCd", colId: "planLvlTpCd", cellClass: "stringType", width: 120, columnGroupShow: "open",
          type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
          cellEditorParams: () => codeComboCellEditorParams("FP_PLAN_LEVEL_TP_CD", planLvlValuesRef, t) },
        { headerName: "FP_VIRTUAL_RES_CNT", field: "virtualResourceCnt", colId: "virtualResourceCnt", width: 120, columnGroupShow: "open",
          type: ["numberColumn", "rightAligned"] }
      ]
    },
    // 수정 정보(audit) 그룹 — Route.js와 동일 FP_COL_AUDIT 그룹 패턴 재사용.
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveResource = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpResourceService.getAll({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setResource([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setResource(results);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const insertRow = () => {
    const newRow = {
      id: null,
      planScope,
      resourceCd: "",
      resourceNm: "",
      descTxt: "",
      displaySeq: 1,
      displayColor: "#ff96c8",
      stageCd: "",
      toolResourceYn: false,
      toolCnt: 1,
      fifoTpCd: "",
      loadYn: true,
      planLvlTpCd: "A",
      virtualResourceCnt: 1,
      jcDivideTpCd: "Y",
      batchResourceYn: false
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeResource = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpResourceService.remove(selectedRows)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveResource();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveResource = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      setProgressOn(false);
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpResourceService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
        retrieveResource();
      });
  };

  const onSelectStage = (row) => {
    const node = stagePopup.node;
    if (node) {
      node.setDataValue("stageCd", row.stageCd);
      node.setDataValue("stageNm", row.stageNm);
      node.setDataValue("plantCd", row.plantCd);
      node.setDataValue("plantNm", row.plantNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveResource();
    } else if (action === "DELETE") {
      removeResource();
    }
  };

  // 화면 간 이동 시 state 전달(old location.state 기반 자동조회)은 Tier2 유보 —
  // component-mapping-table.md §8-1 결론(파급력 낮음, 필요 시 재현 설계 이미 문서화됨).
  // AUTO_LOAD 플래그는 new 관례(항상 자동조회)로 이미 동등 대체됨 — §8-2 결론, 별도 이관 불필요.
  useEffect(() => {
    if (!gridLoading) return;
    retrieveResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      <Box id="fp-resource-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={resource}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <StagePopup
        open={stagePopup.open}
        onClose={() => setStagePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectStage}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Resource);
